import Button from "Components/button";
import { TextWidget } from "Components/optionForm";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import locale from "./passwordManager.locale";
import api from "./redux/api";
import zxcvbn from "zxcvbn";
const repeatPasswordValidationError = { message: locale.PASSWORD_CONFIRMATION_IS_INVALID };
const autocompleteOffInputAttr = { autocomplete: "new-password" };
const hasOnlySpaces = (value) => value && /^\s+$/.test(value);
export default function PasswordManager(props) {
    const [confirmedOldPassword, setConfirmedOldPassword] = useState(undefined);
    const [oldPassword, setOldPassword] = useState(undefined);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [repeatPassword, setRepeatPassword] = useState("");
    const [newPasswordValidationErrors, setNewPasswordValidationError] = useState();
    const handleClose = useCallback(() => {
        props.onClose && props.onClose();
        setConfirmedOldPassword(undefined);
        setOldPassword(undefined);
        setShowNewPassword(false);
        setNewPassword("");
        setRepeatPassword("");
    }, [props.onClose]);
    const handleChangeOldPassword = useCallback((value) => {
        setConfirmedOldPassword(undefined);
        setOldPassword(value);
    }, []);
    const handleChangeNewPassword = useCallback((value) => {
        setNewPassword(value);
    }, []);
    const handleChangeRepeatPassword = useCallback((value) => {
        setRepeatPassword(value);
    }, []);
    const handleSave = useCallback(() => {
        if (oldPassword &&
            confirmedOldPassword &&
            newPassword === repeatPassword &&
            !hasOnlySpaces(newPassword)) {
            api
                .changePassword({
                OldPassword: oldPassword,
                NewPassword: newPassword,
                NewPasswordConfirmation: repeatPassword
            })
                .then(result => {
                if (result.data.state === 0) {
                    props.showInfo && props.showInfo(locale.CHANGE_PASSWORD_SUCCESS);
                    handleClose();
                }
                else {
                    const errorMessage = `${locale.CHANGE_PASSWORD_ERROR}: ${result.data.data}`;
                    props.showError && props.showError(errorMessage);
                }
            });
        }
    }, [
        props.showError,
        props.showInfo,
        confirmedOldPassword,
        oldPassword,
        newPassword,
        repeatPassword
    ]);
    const handleValidateOldPassword = useCallback(({ component }) => {
        const passwordValue = component.instance().option("value");
        passwordValue &&
            api.validatePassword(passwordValue).then(({ data }) => {
                setConfirmedOldPassword(data);
            });
    }, []);
    const oldPasswordValidationError = useMemo(() => {
        if (!oldPassword)
            return { message: oldPassword !== undefined ? locale.OLD_PASSWORD_IS_REQUIRED : "" };
        return { message: confirmedOldPassword === false ? locale.INVALID_PASSWORD : "" };
    }, [oldPassword, confirmedOldPassword]);
    const repeatPasswordIsInvalid = repeatPassword && newPassword !== repeatPassword;
    useEffect(() => {
        if (!newPassword) {
            setNewPasswordValidationError(undefined);
            return undefined;
        }
        if (hasOnlySpaces(newPassword)) {
            setNewPasswordValidationError({ message: locale.ONLY_SPACES });
            return undefined;
        }
        const { score } = zxcvbn(newPassword);
        setNewPasswordValidationError(score < 2 ? { message: locale.ANSWER[score] } : undefined);
        return undefined;
    }, [newPassword]);
    return (React.createElement("form", { className: props.className },
        React.createElement("div", { className: "w-100" },
            React.createElement(TextWidget, { name: "old_password", mode: "password", showPasswordEyeButton: true, onFocusOut: handleValidateOldPassword, label: locale.OLD_PASSWORD, labelMode: "floating", height: 45, value: oldPassword, onChange: handleChangeOldPassword, inputAttr: autocompleteOffInputAttr, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: oldPasswordValidationError, validationStatus: oldPasswordValidationError.message ? "invalid" : "valid" })),
        React.createElement("div", { className: "w-100 mt-gap" },
            React.createElement(TextWidget, { name: "new_password", mode: "password", showPasswordEyeButton: true, label: locale.NEW_PASSWORD, labelMode: "floating", height: 45, value: newPassword, onChange: handleChangeNewPassword, inputAttr: autocompleteOffInputAttr, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: newPasswordValidationErrors, validationStatus: newPasswordValidationErrors ? "invalid" : "valid" })),
        React.createElement("div", { className: "w-100 mt-gap" },
            React.createElement(TextWidget, { name: "repeat_password", label: locale.REPEAT_PASSWORD, labelMode: "floating", height: 45, mode: showNewPassword ? "text" : "password", value: repeatPassword, onChange: handleChangeRepeatPassword, inputAttr: autocompleteOffInputAttr, validationMessageMode: "always", validationMessagePosition: "bottom", validationError: repeatPasswordValidationError, validationStatus: repeatPasswordIsInvalid ? "invalid" : "valid" })),
        React.createElement("div", { className: "mt-24 d-flex justify-content-flex-end with-gap-8" },
            React.createElement(Button, { className: "btn-h-m p-8-24", onClick: handleClose, minWidth: 101 }, locale.CANCEL),
            React.createElement(Button, { onClick: handleSave, disabled: !confirmedOldPassword ||
                    repeatPasswordIsInvalid ||
                    !repeatPassword ||
                    hasOnlySpaces(newPassword), className: "btn-primary btn-h-m p-8-24", minWidth: 123 }, locale.CONFIRM))));
}
